import { normalizeBienForEdit, bienFieldValue, BIEN_API_FIELDS, buildBienUpdate } from "../Services/bienEditModel";
import uiI18n from "../i18n";
import { useTranslation as useUiTranslation } from "react-i18next";
import { getCurrentUser } from "../Services/apiClient";
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  LuArrowLeft,
  LuArrowRight,
  LuCheck,
  LuHouse,
  LuImages,
  LuInfo,
  LuSave,
} from "react-icons/lu";

import ImageUpload from "../components/ImageUpload";
import SectionInformation from "../components/SectionInformation";
import SectionEdit from "../components/SectionEdit";
import Spinner from "../components/Spinner";

import {
  getBienById,
  updateBien,
} from "../Services/biensService";

const TOTAL_STEPS = 3;

const INITIAL_FORM_DATA = { ...normalizeBienForEdit({}), images: [] };

const STEPS = [
  {
    id: 1,
    get label() { return uiI18n.t("ui.property"); },
    icon: LuHouse,
  },
  {
    id: 2,
    get label() { return uiI18n.t("ui.information"); },
    icon: LuInfo,
  },
  {
    id: 3,
    get label() { return uiI18n.t("ui.photos_290"); },
    icon: LuImages,
  },
];

function EditListing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bienID } = useParams();

  const [formData, setFormData] =
    useState(INITIAL_FORM_DATA);

  const [
    currentSection,
    setCurrentSection,
  ] = useState(1);

  const [loading, setLoading] =
    useState(true);

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [isModified, setIsModified] =
    useState(false);

  /*
  |--------------------------------------------------------------------------
  | Charger l'annonce
  |--------------------------------------------------------------------------
  */

  const loadListing =
    useCallback(async () => {
      const t = (...args) => uiI18n.t(...args);
      if (!bienID) {
        setError(
          t(
            "edit.invalid_id",
            "Identifiant de l'annonce invalide."
          )
        );

        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const listing =
          normalizeBienForEdit(await getBienById(bienID));

        const user = await getCurrentUser();
        if (listing?.uid !== user.uid) throw new Error(uiI18n.t("ui.you_do_not_own_this_listing"));

        if (!listing) {
          throw new Error(
            t(
              "edit.not_found",
              "Cette annonce est introuvable."
            )
          );
        }

        const existingImages =
          listing.urlImages
            ? String(
                listing.urlImages
              )
                .split(",")
                .map((image) =>
                  image.trim()
                )
                .filter(Boolean)
            : [];

        setFormData({ ...listing, images: existingImages });

        setIsModified(false);
      } catch (requestError) {
        console.error(
          "Erreur lors du chargement du bien :",
          requestError
        );

        setError(
          requestError?.message ||
            t(
              "edit.load_error",
              "Impossible de récupérer les informations de cette annonce."
            )
        );
      } finally {
        setLoading(false);
      }
    }, [bienID]);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  /*
  |--------------------------------------------------------------------------
  | Modification générique
  |--------------------------------------------------------------------------
  */

  const onChange = (event) => {
    const { id, value, type, checked } = event.currentTarget;
    if (!Object.prototype.hasOwnProperty.call(BIEN_API_FIELDS, id)) return;
    const nextValue = bienFieldValue(id, type === "checkbox" ? checked : value);
    setFormData(previous => ({ ...previous, [id]: nextValue }));
    setIsModified(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Wrapper setFormData
  |--------------------------------------------------------------------------
  |
  | Important :
  | SectionEdit et SectionInformation utilisent directement setFormData.
  | Ce wrapper permet de détecter également leurs modifications.
  |
  */

  const updateFormData = (
    valueOrUpdater
  ) => {
    setFormData(
      (previousValue) => {
        const nextValue =
          typeof valueOrUpdater ===
          "function"
            ? valueOrUpdater(
                previousValue
              )
            : valueOrUpdater;

        return nextValue;
      }
    );

    setIsModified(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Images
  |--------------------------------------------------------------------------
  */

  const onImagesChange = (
    imageFiles
  ) => {
    const normalizedImages =
      Array.isArray(imageFiles)
        ? imageFiles
        : [];

    const existingImages =
      normalizedImages.filter(
        (image) =>
          typeof image === "string"
      );

    setFormData(
      (previousValue) => ({
        ...previousValue,

        images:
          normalizedImages,

        urlImages: existingImages.join(","),
      })
    );

    setIsModified(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Navigation
  |--------------------------------------------------------------------------
  */

  const scrollToFormTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleContinue = (
    event
  ) => {
    event?.preventDefault();

    setCurrentSection(
      (previousValue) =>
        Math.min(
          previousValue + 1,
          TOTAL_STEPS
        )
    );

    scrollToFormTop();
  };

  const handlePrevious = (
    event
  ) => {
    event?.preventDefault();

    setCurrentSection(
      (previousValue) =>
        Math.max(
          previousValue - 1,
          1
        )
    );

    scrollToFormTop();
  };

  /*
  |--------------------------------------------------------------------------
  | Mise à jour
  |--------------------------------------------------------------------------
  */

  const updateClick = async (
    event
  ) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    if (!isModified) {
      toast.info(
        t(
          "edit.no_changes",
          "Aucune modification détectée."
        )
      );

      return;
    }

    setSubmitting(true);

    try {
      const payload = buildBienUpdate(formData, formData.images);
      const result = await updateBien(payload.data, payload.files);

      toast.success(
        result?.Message ||
          result?.message ||
          t(
            "edit.success",
            "L'annonce a été mise à jour."
          )
      );

      setIsModified(false);

      navigate("/myAds");
    } catch (requestError) {
      console.error(
        "Erreur mise à jour annonce :",
        requestError
      );

      toast.error(
        requestError?.message ||
          t(
            "edit.update_error",
            "Impossible de mettre à jour l'annonce."
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Progression
  |--------------------------------------------------------------------------
  */

  const progressPercentage =
    ((currentSection - 1) /
      (TOTAL_STEPS - 1)) *
    100;

  /*
  |--------------------------------------------------------------------------
  | Chargement
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Erreur
  |--------------------------------------------------------------------------
  */

  if (error) {
    return (
      <main className="min-h-[60vh] bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-xl rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
          <h1 className="text-xl font-black text-red-700">
            {t(
              "edit.error_title",
              "Impossible de modifier l'annonce"
            )}
          </h1>

          <p className="mt-3 text-sm leading-6 text-red-600">
            {error}
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/myAds")
            }
            className="
              mt-6
              inline-flex
              h-11
              items-center
              justify-center
              rounded-xl
              bg-slate-950
              px-5
              text-sm
              font-bold
              text-white
              transition
              hover:bg-green-600
            "
          >
            {t(
              "edit.back_ads",
              "Retour à mes annonces"
            )}
          </button>
        </div>
      </main>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Page
  |--------------------------------------------------------------------------
  */

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <div className="mx-auto max-w-5xl">

        {/* En-tête */}

        <header className="mb-8 text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-green-600">
            Lina Immo
          </p>

          <h1 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl">
            {t(
              "update_ad",
              "Modifier l'annonce"
            )}
          </h1>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            {t(
              "edit.description",
              "Modifiez les informations de votre annonce puis enregistrez vos changements."
            )}
          </p>

          {/* Modification détectée */}

          {isModified && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-xs font-bold text-amber-700">
              <span className="h-2 w-2 rounded-full bg-amber-500" />

              {t(
                "edit.unsaved_changes",
                "Modifications non enregistrées"
              )}
            </div>
          )}
        </header>

        {/* Progression */}

        <div className="mb-7 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm sm:p-6">
          <div className="relative">

            {/* Ligne */}

            <div className="absolute start-0 end-0 top-5 hidden h-1 rounded-full bg-slate-100 sm:block">
              <div
                className="h-full rounded-full bg-green-600 transition-all duration-500"
                style={{
                  width: `${progressPercentage}%`,
                }}
              />
            </div>

            {/* Étapes */}

            <div className="relative grid grid-cols-3 gap-3">
              {STEPS.map((step) => {
                const Icon =
                  step.icon;

                const completed =
                  step.id <
                  currentSection;

                const active =
                  step.id ===
                  currentSection;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => {
                      /*
                        Autorise à revenir
                        vers une étape précédente.

                        On évite de sauter
                        directement vers une étape
                        suivante.
                      */

                      if (
                        step.id <=
                        currentSection
                      ) {
                        setCurrentSection(
                          step.id
                        );
                      }
                    }}
                    className="flex flex-col items-center text-center"
                  >
                    <span
                      className={`
                        relative
                        z-10
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-full
                        border-2
                        transition

                        ${
                          completed
                            ? "border-green-600 bg-green-600 text-white"
                            : active
                              ? "border-green-600 bg-white text-green-600 shadow-lg shadow-green-600/15"
                              : "border-slate-200 bg-white text-slate-400"
                        }
                      `}
                    >
                      {completed ? (
                        <LuCheck
                          size={17}
                        />
                      ) : (
                        <Icon
                          size={17}
                        />
                      )}
                    </span>

                    <span
                      className={`
                        mt-2
                        text-[11px]
                        font-bold
                        sm:text-xs

                        ${
                          active
                            ? "text-green-700"
                            : completed
                              ? "text-slate-700"
                              : "text-slate-400"
                        }
                      `}
                    >
                      {t(
                        `edit.steps.${step.id}`,
                        step.label
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Formulaire */}

        <form
          onSubmit={updateClick}
          className="
            rounded-3xl
            border
            border-slate-100
            bg-white
            p-5
            shadow-xl
            shadow-slate-900/5
            sm:p-8
          "
        >
          {/* =================================================
              ÉTAPE 1
          ================================================= */}

          {currentSection === 1 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 1 })}
              title={t(
                "edit.property_title",
                "Informations du bien"
              )}
              description={t(
                "edit.property_description",
                "Modifiez les informations principales de votre annonce."
              )}
            >
              <SectionEdit
                formData={
                  formData
                }
                setFormData={
                  updateFormData
                }
                onChange={
                  onChange
                }
              />

              <StepActions
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
                onNext={
                  handleContinue
                }
              />
            </StepContainer>
          )}

          {/* =================================================
              ÉTAPE 2
          ================================================= */}

          {currentSection === 2 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 2 })}
              title={t(
                "edit.additional_title",
                "Informations supplémentaires"
              )}
              description={t(
                "edit.additional_description",
                "Complétez les caractéristiques et les informations complémentaires du bien."
              )}
            >
              <SectionInformation geographicSearch optionalPrice
                formData={
                  formData
                }
                setFormData={
                  updateFormData
                }
                onChange={
                  onChange
                }
              />

              <StepActions
                previousLabel={t(
                  "previous",
                  "Précédent"
                )}
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
                onPrevious={
                  handlePrevious
                }
                onNext={
                  handleContinue
                }
              />
            </StepContainer>
          )}

          {/* =================================================
              ÉTAPE 3
          ================================================= */}

          {currentSection === 3 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 3 })}
              title={t(
                "edit.photos_title",
                "Photos de l'annonce"
              )}
              description={t(
                "edit.photos_description",
                "Conservez, supprimez ou ajoutez des photos à votre annonce."
              )}
            >
              {/* Upload */}

              <div
                className="
                  rounded-3xl
                  border
                  border-dashed
                  border-slate-300
                  bg-slate-50/70
                  p-4
                  transition
                  hover:border-green-300
                  hover:bg-green-50/30
                  sm:p-6
                "
              >
                <div className="mb-5 flex items-start gap-3">

                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-green-100 text-green-600">
                    <LuImages
                      size={21}
                    />
                  </span>

                  <div>
                    <h3 className="text-sm font-black text-slate-900">
                      {t(
                        "edit.manage_photos",
                        "Gérer les photos"
                      )}
                    </h3>

                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {t(
                        "edit.manage_photos_description",
                        "Les photos existantes sont conservées sauf si vous les supprimez. Vous pouvez également en ajouter de nouvelles."
                      )}
                    </p>
                  </div>
                </div>

                <ImageUpload
                  initialImages={formData.images}
                  onImagesChange={
                    onImagesChange
                  }
                  folder={
                    formData.folder
                  }
                />
              </div>

              {/* Boutons dernière étape */}

              <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-between">

                <button
                  type="button"
                  onClick={
                    handlePrevious
                  }
                  disabled={
                    submitting
                  }
                  className="
                    inline-flex
                    h-12
                    items-center
                    justify-center
                    gap-2
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    px-6
                    text-sm
                    font-bold
                    text-slate-700
                    transition
                    hover:border-slate-300
                    hover:bg-slate-100
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                  "
                >
                  <LuArrowLeft
                    size={17}
                    className="rtl:rotate-180"
                  />

                  {t(
                    "previous",
                    "Précédent"
                  )}
                </button>

                <button
                  type="submit"
                  disabled={
                    submitting ||
                    !isModified
                  }
                  className="
                    inline-flex
                    h-12
                    items-center
                    justify-center
                    gap-2
                    rounded-2xl
                    bg-green-600
                    px-7
                    text-sm
                    font-black
                    text-white
                    shadow-lg
                    shadow-green-600/20
                    transition-all
                    hover:-translate-y-0.5
                    hover:bg-green-700
                    disabled:cursor-not-allowed
                    disabled:bg-slate-300
                    disabled:shadow-none
                  "
                >
                  {submitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                      {t(
                        "edit.updating",
                        "Mise à jour..."
                      )}
                    </>
                  ) : (
                    <>
                      <LuSave
                        size={17}
                      />

                      {t(
                        "update_ad",
                        "Mettre à jour l'annonce"
                      )}
                    </>
                  )}
                </button>
              </div>
            </StepContainer>
          )}
        </form>
      </div>
    </main>
  );
}

/*
|--------------------------------------------------------------------------
| STEP CONTAINER
|--------------------------------------------------------------------------
*/

function StepContainer({
  eyebrow,
  title,
  description,
  children,
}) {
  useUiTranslation();
  return (
    <section>
      <div className="mb-7">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-green-600">
          {eyebrow}
        </p>

        <h2 className="mt-2 text-2xl font-black text-slate-950">
          {title}
        </h2>

        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
          {description}
        </p>
      </div>

      {children}
    </section>
  );
}

/*
|--------------------------------------------------------------------------
| STEP ACTIONS
|--------------------------------------------------------------------------
*/

function StepActions({
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
}) {
  useUiTranslation();
  return (
    <div
      className={`
        mt-8
        flex
        flex-col-reverse
        gap-3
        border-t
        border-slate-100
        pt-6
        sm:flex-row

        ${
          onPrevious
            ? "sm:justify-between"
            : "sm:justify-end"
        }
      `}
    >
      {onPrevious && (
        <button
          type="button"
          onClick={
            onPrevious
          }
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            border
            border-slate-200
            bg-white
            px-6
            text-sm
            font-bold
            text-slate-700
            transition
            hover:border-slate-300
            hover:bg-slate-100
          "
        >
          <LuArrowLeft
            size={17}
            className="rtl:rotate-180"
          />

          {previousLabel}
        </button>
      )}

      {onNext && (
        <button
          type="button"
          onClick={onNext}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            bg-slate-950
            px-7
            text-sm
            font-black
            text-white
            shadow-lg
            transition-all
            hover:-translate-y-0.5
            hover:bg-green-600
            focus:outline-none
            focus:ring-4
            focus:ring-green-500/20
          "
        >
          {nextLabel}

          <LuArrowRight
            size={17}
            className="rtl:rotate-180"
          />
        </button>
      )}
    </div>
  );
}

export default EditListing;